// Only English ships in the marketing bundle (it is the type source and the
// fallback); every other locale is its own chunk, loaded before first render
// by boot-marketing (ensureLocaleContent) or on a language switch. All seven
// up front were ~86 KiB gzipped of the page's initial JavaScript.
import en from "./components/sections/resources/en/site.json";
import type { SectionIllustrationOverrides } from "./assets/illustrations";

type BaseLocalizedContent = typeof en;

interface HeaderContent {
    logoAlt?: string;
    subscribeLabel?: string;
}

interface FooterNavigationLink {
    label?: string;
    section?: string;
    href?: string;
}

interface FooterLink {
    label?: string;
    href?: string;
}

interface FooterContent {
    cta?: {
        eyebrow?: string;
        title?: string;
        description?: string;
        button?: {
            label?: string;
            href?: string;
        };
    };
    brand?: {
        name?: string;
        description?: string;
        email?: string;
        emailLabel?: string;
    };
    social?: Array<{
        id?: string;
        label?: string;
        href?: string;
    }>;
    navigation?: {
        product?: {
            title?: string;
            items?: FooterNavigationLink[];
        };
        resources?: {
            title?: string;
            items?: FooterNavigationLink[];
        };
        status?: {
            title?: string;
            description?: string;
            links?: FooterLink[];
        };
    };
    legal?: {
        text?: string;
        localeLabel?: string;
        links?: FooterLink[];
    };
}

type ExtendedSections = {
    hero: BaseLocalizedContent["hero"] & {
        contextDetailOverline?: string;
        contextExplorePrefix?: string;
    };
    features: BaseLocalizedContent["features"] & {
        floatingBadge?: string;
    };
    testimonials: BaseLocalizedContent["testimonials"] & {
        badge?: string;
    };
};

type OptionalContent = {
    illustrations?: SectionIllustrationOverrides;
    app?: {
        notifications?: {
            title?: string;
            body?: string;
            icon?: string;
        };
    };
    theme?: {
        sectionStops?: string[];
    };
    footer?: FooterContent;
    header?: HeaderContent;
};

export type LocalizedContent = Omit<BaseLocalizedContent, keyof ExtendedSections> & ExtendedSections & OptionalContent;

export type SupportedLocale = "en" | "es" | "pt" | "fr" | "de" | "it" | "uk";

export const DEFAULT_LOCALE: SupportedLocale = "en";

export const SUPPORTED_LOCALES: SupportedLocale[] = ["en", "es", "pt", "fr", "de", "it", "uk"];

const LOADERS: Record<SupportedLocale, () => Promise<{ default: unknown }>> = {
    en: () => Promise.resolve({ default: en }),
    es: () => import(/* webpackChunkName: "site-content-es" */ "./components/sections/resources/es/site.json"),
    pt: () => import(/* webpackChunkName: "site-content-pt" */ "./components/sections/resources/pt/site.json"),
    fr: () => import(/* webpackChunkName: "site-content-fr" */ "./components/sections/resources/fr/site.json"),
    de: () => import(/* webpackChunkName: "site-content-de" */ "./components/sections/resources/de/site.json"),
    it: () => import(/* webpackChunkName: "site-content-it" */ "./components/sections/resources/it/site.json"),
    uk: () => import(/* webpackChunkName: "site-content-uk" */ "./components/sections/resources/uk/site.json"),
};

/** Locales whose content is in memory. */
const CONTENT_MAP: Partial<Record<SupportedLocale, LocalizedContent>> = { en: en as LocalizedContent };

export const isLocaleContentLoaded = (locale: SupportedLocale): boolean => Boolean(CONTENT_MAP[locale]);

/** Waits between attempts to load a language's content chunk. */
const RETRY_DELAYS_MS = [400, 1200, 3000];

/** Loads a locale's content chunk once; resolves when it can be rendered.
 *  A failed request is retried (webpack forgets a failed chunk, so a new
 *  attempt re-requests it): one bad response — e.g. a request that reaches an
 *  older pod while a deploy rolls out — used to leave the visitor on the
 *  English fallback for the whole visit while the flag showed their language.
 *  If every attempt fails, English stays the fallback. */
export const ensureLocaleContent = async (locale: SupportedLocale): Promise<void> => {
    for (let attempt = 0; !CONTENT_MAP[locale]; attempt++) {
        try {
            const mod = await LOADERS[locale]();
            CONTENT_MAP[locale] = ((mod as { default?: unknown }).default ?? mod) as LocalizedContent;
        } catch {
            if (attempt >= RETRY_DELAYS_MS.length) return;
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
        }
    }
};

const toCandidateKey = (locale?: string): string | undefined => {
    if (!locale) {
        return undefined;
    }
    return locale.split("-")[0].toLowerCase();
};

export const findSupportedLocale = (locale?: string): SupportedLocale | undefined => {
    const candidate = toCandidateKey(locale);
    if (!candidate) {
        return undefined;
    }

    if ((SUPPORTED_LOCALES as string[]).includes(candidate)) {
        return candidate as SupportedLocale;
    }

    return undefined;
};

export const resolveContentBundle = (
    preferredLocale?: string
): { locale: SupportedLocale; content: LocalizedContent } => {
    const browserLocales: string[] = [];
    if (typeof navigator !== "undefined") {
        if (navigator.language) {
            browserLocales.push(navigator.language);
        }
        if (Array.isArray((navigator as Navigator & { languages?: string[] }).languages)) {
            browserLocales.push(
                ...((navigator as Navigator & { languages: string[] }).languages.filter(Boolean))
            );
        }
    }

    const candidates: Array<string | undefined> = [preferredLocale, ...browserLocales];

    for (const candidate of candidates) {
        const supported = findSupportedLocale(candidate);
        if (supported && CONTENT_MAP[supported]) {
            return {
                locale: supported,
                content: CONTENT_MAP[supported] as LocalizedContent
            };
        }
    }

    return {
        locale: DEFAULT_LOCALE,
        content: CONTENT_MAP[DEFAULT_LOCALE] as LocalizedContent
    };
};

export const resolveContent = (preferredLocale?: string): LocalizedContent => {
    return resolveContentBundle(preferredLocale).content;
};
